import { LabelPrinter } from '../models/label-printer.model';
import { LabelPrinterGroup } from '../models/label-printer-group.model';

/**
 * Route-context printer filtering for the label printer picker.
 * - Contexts match the route by prefix on segment boundaries only, with any
 *   query params/fragment stripped ('/plastics/ship' does not match '/plastics/shipping').
 * - Deepest match wins: only groups whose best matching context has the most
 *   segments apply, so a page-specific group beats a broad module group like
 *   '/plastics'. Groups tied at the winning depth => union of their
 *   labelPrinterIds, filtered to loaded label printers (dangling ids are invisible).
 * - No matching groups => all printers (fallback-open).
 * - Always sorted by printer name.
 */
export function filterLabelPrintersByRoute(
    labelPrinters: LabelPrinter[],
    labelPrinterGroups: LabelPrinterGroup[],
    routeUrl: string,
    fallbackToAll = true,
): LabelPrinter[] {
    const url = routeUrl.split('?')[0].split('#')[0];
    const groupMatchDepths = labelPrinterGroups.map(labelPrinterGroup => {
        const matchDepths = (labelPrinterGroup.contexts ?? [])
            .map(rawContext => rawContext.replace(/\/+$/, ''))
            .filter(context => !!context && (url === context || url.startsWith(context + '/')))
            .map(context => context.split('/').filter(segment => segment.length > 0).length);
        return { labelPrinterGroup, depth: matchDepths.length === 0 ? 0 : Math.max(...matchDepths) };
    });
    const winningDepth = Math.max(0, ...groupMatchDepths.map(groupMatchDepth => groupMatchDepth.depth));
    let result: LabelPrinter[];
    if (winningDepth === 0) {
        result = fallbackToAll ? [...labelPrinters] : [];
    } else {
        const labelPrinterIds = new Set(
            groupMatchDepths
                .filter(groupMatchDepth => groupMatchDepth.depth === winningDepth)
                .flatMap(groupMatchDepth => groupMatchDepth.labelPrinterGroup.labelPrinterIds ?? [])
        );
        result = labelPrinters.filter(labelPrinter => labelPrinterIds.has(labelPrinter.id));
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
}
